import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import './MyPage.css';

interface ReservationDetail {
  id: string;
  property_id?: string;
  property_name: string;
  guest_name: string;
  guest_email: string;
  checkin_date: string;
  checkout_date: string;
  guest_count: number;
  total_price: number;
  currency: string;
  status: string;
  payment_method: string | null;
  created_at: string;
  cancel_requested?: number;
  cancel_requested_at?: string | null;
  cancellation_policy?: {
    free_cancel_until_days: number;
    free_cancel_until: string;
    is_free_cancelable_now: boolean;
    note: string;
  };
}

interface MemberUser {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  country: string | null;
  address: string | null;
}

// 認証は HttpOnly Cookie（member_session）で行う。localStorage のトークンは廃止。

function statusLabel(status: string, t: (k: string) => string) {
  const known = ['PENDING_PAYMENT','PENDING_TRANSFER','CONFIRMED','CANCELLED','EXPIRED','REFUNDED'];
  return known.includes(status) ? t(`mypage.status.${status}`) : status;
}

// 予約番号を大きく表示するブロック（コピーボタン付き）
function ReservationNumber({ id }: { id: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* クリップボード不可の環境では何もしない */
    }
  };
  return (
    <div className="resv-number">
      <div className="resv-number-label">{t('mypage.bookingNumber')}</div>
      <div className="resv-number-row">
        <span className="resv-number-value">{id}</span>
        <button type="button" className="resv-number-copy" onClick={copy}>
          {copied ? t('mypage.copied') : t('mypage.copy')}
        </button>
      </div>
      <small className="resv-number-hint">{t('mypage.bookingNumberHint')}</small>
    </div>
  );
}

// この予約についてお問い合わせフォームを開くリンク（予約番号などを事前入力）
//   cat: カテゴリーの番号（0=ご予約について, 2=キャンセル・変更について）
function contactLink(r: ReservationDetail): string {
  const q = new URLSearchParams({
    reservation: r.id,
    email: r.guest_email || '',
    name: r.guest_name || '',
    property_id: r.property_id || '',
    cat: Number(r.cancel_requested) === 1 || ['CANCELLED', 'REFUNDED'].includes(r.status) ? '2' : '0',
  });
  return `/contact?${q.toString()}`;
}

// キャンセル申請できる予約か（確定/支払待ち・未申請・チェックイン前）
function canRequestCancel(r: ReservationDetail): boolean {
  const today = new Date().toISOString().split('T')[0];
  return ['CONFIRMED', 'PENDING_TRANSFER', 'PENDING_PAYMENT'].includes(r.status) &&
    Number(r.cancel_requested) !== 1 &&
    r.checkin_date > today;
}

function fmtMoney(n: number, currency = 'JPY') {
  return new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency,
    maximumFractionDigits: currency === 'JPY' ? 0 : 2,
  }).format(n);
}

function MyPage() {
  const { t } = useTranslation();

  // ログイン状態
  const [user, setUser] = useState<MemberUser | null>(null);
  const [myReservations, setMyReservations] = useState<ReservationDetail[]>([]);
  const [authChecking, setAuthChecking] = useState(true);

  // ログインフォーム
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);

  // ゲスト照会フォーム
  const [reservationId, setReservationId] = useState('');
  const [email, setEmail] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [reservation, setReservation] = useState<ReservationDetail | null>(null);

  // キャンセル申請
  const [cancelTarget, setCancelTarget] = useState<ReservationDetail | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelDoneId, setCancelDoneId] = useState<string | null>(null);

  const openCancel = (r: ReservationDetail) => {
    setCancelTarget(r);
    setCancelReason('');
    setCancelError(null);
  };

  const submitCancel = async () => {
    if (!cancelTarget) return;
    setCancelLoading(true);
    setCancelError(null);
    try {
      const res = await fetch(`/api/reservations/${cancelTarget.id}/cancel-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: cancelTarget.guest_email, reason: cancelReason.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || t('mypage.cancelRequestFailed'));
      const mark = (r: ReservationDetail) =>
        r.id === cancelTarget.id ? { ...r, cancel_requested: 1 } : r;
      setMyReservations((list) => list.map(mark));
      setReservation((r) => (r ? mark(r) : r));
      setCancelDoneId(cancelTarget.id);
      setCancelTarget(null);
    } catch (err: any) {
      setCancelError(err?.message || String(err));
    } finally {
      setCancelLoading(false);
    }
  };

  // キャンセル申請ボタン／申請済み表示
  const cancelArea = (r: ReservationDetail) => (
    <div className="cancel-area">
      <Link to={contactLink(r)} className="btn btn-contact">
        {t('mypage.contactAboutBooking')}
      </Link>
      {r.status === 'REFUNDED' ? (
        <div className="cancel-result approved">
          <strong>{t('mypage.cancelApproved')}</strong>
          <p>{t('mypage.refundedFull', { amount: fmtMoney(r.total_price, r.currency) })}</p>
        </div>
      ) : r.status === 'CANCELLED' ? (
        <div className="cancel-result approved">
          <strong>{t('mypage.cancelApproved')}</strong>
          <p>
            {r.payment_method === 'bank_transfer'
              ? t('mypage.refundSeparate')
              : t('mypage.noRefund')}
          </p>
        </div>
      ) : Number(r.cancel_requested) === 1 ? (
        <p className="cancel-requested-note">
          {t('mypage.cancelRequestReceived')}
        </p>
      ) : canRequestCancel(r) ? (
        <button type="button" className="btn btn-cancel-request" onClick={() => openCancel(r)}>
          {t('mypage.requestCancel')}
        </button>
      ) : null}
      {cancelDoneId === r.id && Number(r.cancel_requested) === 1 && (
        <p className="cancel-done">{t('mypage.requestSent')}</p>
      )}
    </div>
  );

  // 申請確認ダイアログ
  const cancelDialog = cancelTarget && (
    <div className="cancel-modal-overlay" onClick={() => !cancelLoading && setCancelTarget(null)}>
      <div className="cancel-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{t('mypage.cancelRequestTitle')}</h3>
        <p>
          {cancelTarget.property_name}<br />
          {cancelTarget.checkin_date} 〜 {cancelTarget.checkout_date}（{cancelTarget.guest_count}{t('mypage.guestsSuffix')}）
        </p>
        {cancelTarget.cancellation_policy && (
          <p className={cancelTarget.cancellation_policy.is_free_cancelable_now ? 'cancel-free' : 'cancel-nonrefund'}>
            {cancelTarget.cancellation_policy.is_free_cancelable_now
              ? t('mypage.freeCancelUntil', { date: cancelTarget.cancellation_policy.free_cancel_until })
              : t('mypage.noFreeCancelAfter', { date: cancelTarget.cancellation_policy.free_cancel_until })}
          </p>
        )}
        <label className="cancel-reason-label">
          {t('mypage.cancelReason')}
          <textarea
            rows={3}
            value={cancelReason}
            maxLength={1000}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </label>
        <small>{t('mypage.cancelConfirmNote')}</small>
        {cancelError && <p className="lookup-error">{cancelError}</p>}
        <div className="cancel-modal-actions">
          <button type="button" className="btn btn-outline" disabled={cancelLoading} onClick={() => setCancelTarget(null)}>
            {t('mypage.back')}
          </button>
          <button type="button" className="btn btn-cancel-request" disabled={cancelLoading} onClick={submitCancel}>
            {cancelLoading ? t('mypage.sending') : t('mypage.requestCancel')}
          </button>
        </div>
      </div>
    </div>
  );

  // 起動時: Cookie セッションがあれば会員情報を取得
  useEffect(() => {
    (async () => {
      try {
        const meRes = await fetch('/api/auth/me', { credentials: 'include' });
        if (!meRes.ok) throw new Error('token invalid');
        const meData = await meRes.json();
        setUser(meData.user);
        await loadMyReservations();
      } catch {
        // 未ログイン or セッション無効
      } finally {
        setAuthChecking(false);
      }
    })();
  }, []);

  const loadMyReservations = async () => {
    const res = await fetch('/api/auth/reservations', { credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      setMyReservations(data.reservations || []);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    if (!loginEmail.trim() || !loginPassword) {
      setLoginError(t('booking.loginRequired'));
      return;
    }
    setLoginLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: loginEmail.trim(), password: loginPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || t('booking.loginFailed'));
      setUser(data.user);
      await loadMyReservations();
      setLoginPassword('');
    } catch (err: any) {
      setLoginError(err?.message || t('booking.loginFailed'));
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    void fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
    setUser(null);
    setMyReservations([]);
  };

  const lookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reservationId.trim() || !email.trim()) {
      setLookupError(t('mypage.lookupMissing'));
      return;
    }
    setLookupLoading(true);
    setLookupError(null);
    setReservation(null);
    try {
      const params = new URLSearchParams({
        reservation_id: reservationId.trim(),
        email: email.trim(),
      });
      const res = await fetch(`/api/reservations/lookup?${params}`);
      if (res.status === 404) {
        throw new Error(t('mypage.lookupNotFound'));
      }
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || t('mypage.lookupFailed'));
      }
      const data = await res.json();
      setReservation(data);
    } catch (err: any) {
      setLookupError(err?.message || String(err));
    } finally {
      setLookupLoading(false);
    }
  };

  if (authChecking) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  // ============ ログイン済み: 会員ダッシュボード ============
  if (user) {
    return (
      <div className="mypage">
        <div className="container mypage-container">
          <div className="mypage-head">
            <div>
              <h1 className="mypage-title">My Page</h1>
              <p className="mypage-subtitle">{t('mypage.greeting', { name: user.name, email: user.email })}</p>
            </div>
            <button className="logout-btn" onClick={handleLogout}>
              {t('mypage.logout')}
            </button>
          </div>

          {/* プロフィール */}
          <section className="member-profile">
            <h2>{t('mypage.memberInfo')}</h2>
            <dl className="detail-list">
              <dt>{t('mypage.name')}</dt><dd>{user.name}</dd>
              <dt>{t('mypage.email')}</dt><dd>{user.email}</dd>
              <dt>{t('mypage.phone')}</dt><dd>{user.phone || '—'}</dd>
              <dt>{t('mypage.country')}</dt><dd>{user.country || '—'}</dd>
              <dt>{t('mypage.address')}</dt><dd>{user.address || '—'}</dd>
            </dl>
          </section>

          {/* 予約一覧 */}
          <section className="member-reservations">
            <h2>{t('mypage.reservationsTitle', { count: myReservations.length })}</h2>
            {myReservations.length === 0 ? (
              <p className="no-data">{t('mypage.noReservations')}</p>
            ) : (
              <div className="reservation-cards">
                {myReservations.map((r) => (
                  <div key={r.id} className="reservation-card">
                    <ReservationNumber id={r.id} />
                    <div className="rc-head">
                      <span className="rc-property">{r.property_name}</span>
                      <span className={`status-badge ${r.status.toLowerCase()}`}>
                        {statusLabel(r.status, t)}
                      </span>
                    </div>
                    <div className="rc-dates">
                      {r.checkin_date} 〜 {r.checkout_date}（{r.guest_count}{t('mypage.guestsSuffix')}）
                    </div>
                    <div className="rc-foot">
                      <span />
                      <span className="amount">{fmtMoney(r.total_price, r.currency)}</span>
                    </div>
                    {cancelArea(r)}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
        {cancelDialog}
      </div>
    );
  }

  // ============ 未ログイン: ログイン + ゲスト照会 ============
  return (
    <div className="mypage">
      <div className="container mypage-container">
        <h1 className="mypage-title">My Page</h1>

        {/* 会員ログイン */}
        <section className="mypage-section">
          <h2>{t('booking.memberLogin')}</h2>
          <form className="lookup-form" onSubmit={handleLogin}>
            <div className="lookup-field">
              <label htmlFor="le">{t('booking.email')}</label>
              <input
                id="le"
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="guest@example.com"
              />
            </div>
            <div className="lookup-field">
              <label htmlFor="lp">{t('booking.memberPassword')}</label>
              <input
                id="lp"
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
              />
            </div>
            <div className="lookup-submit">
              <button type="submit" className="btn btn-primary" disabled={loginLoading}>
                {loginLoading ? t('booking.loginLoading') : t('booking.memberLogin')}
              </button>
            </div>
          </form>
          {loginError && <p className="lookup-error">{loginError}</p>}
        </section>

        <div className="mypage-divider"><span>{t('mypage.or')}</span></div>

        {/* ゲスト向け予約照会 */}
        <section className="mypage-section">
          <h2>{t('mypage.lookupTitle')}</h2>
          <p className="mypage-subtitle">
            {t('mypage.lookupDesc')}
          </p>
          <form className="lookup-form" onSubmit={lookup}>
            <div className="lookup-field">
              <label htmlFor="rid">{t('mypage.bookingNumber')}</label>
              <input
                id="rid"
                type="text"
                placeholder={t('mypage.ridPlaceholder')}
                value={reservationId}
                onChange={(e) => setReservationId(e.target.value)}
              />
            </div>
            <div className="lookup-field">
              <label htmlFor="email">{t('mypage.email')}</label>
              <input
                id="email"
                type="email"
                placeholder={t('mypage.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="lookup-submit">
              <button type="submit" className="btn btn-primary" disabled={lookupLoading}>
                {lookupLoading ? t('mypage.lookingUp') : t('mypage.lookup')}
              </button>
            </div>
          </form>

          {lookupError && <p className="lookup-error">{lookupError}</p>}

          {reservation && (
            <section className="reservation-detail">
              <div className="detail-head">
                <h2>{t('mypage.reservationDetails')}</h2>
                <span className={`status-badge ${reservation.status.toLowerCase()}`}>
                  {statusLabel(reservation.status, t)}
                </span>
              </div>
              <ReservationNumber id={reservation.id} />
              <dl className="detail-list">
                <dt>{t('mypage.propertyName')}</dt>
                <dd>{reservation.property_name}</dd>
                <dt>{t('mypage.guestName')}</dt>
                <dd>{reservation.guest_name}</dd>
                <dt>{t('mypage.email')}</dt>
                <dd>{reservation.guest_email}</dd>
                <dt>{t('mypage.checkin')}</dt>
                <dd>{reservation.checkin_date}</dd>
                <dt>{t('mypage.checkout')}</dt>
                <dd>{reservation.checkout_date}</dd>
                <dt>{t('mypage.guests')}</dt>
                <dd>{reservation.guest_count}{t('mypage.guestsSuffix')}</dd>
                <dt>{t('mypage.amount')}</dt>
                <dd className="amount">{fmtMoney(reservation.total_price, reservation.currency)}</dd>
                {reservation.cancellation_policy && (
                  <>
                    <dt>{t('mypage.cancellation')}</dt>
                    <dd>
                      {t('mypage.freeUntil', { date: reservation.cancellation_policy.free_cancel_until })}
                      <br />
                      <small>{t('mypage.noRefundAfter')}</small>
                    </dd>
                  </>
                )}
              </dl>
              {cancelArea(reservation)}
            </section>
          )}
        </section>
      </div>
      {cancelDialog}
    </div>
  );
}

export default MyPage;
